import React, { useState } from "react";
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  InputAdornment,
  Link,
  Alert,
  CircularProgress,
  MenuItem,
} from "@mui/material";

import {
  EmailOutlined,
  LockOutlined,
  WorkOutlineOutlined,
} from "@mui/icons-material";

import { registerUser } from "../api/authApi";

const ALLOWED_EMAIL_DOMAIN = "hirepeaksconsulting.com";

const Signup = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    role: "",
  });

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    setError("");

    if (
      !formData.email ||
      !formData.password ||
      !formData.confirmPassword ||
      !formData.role
    ) {
      setError("Please fill in all fields.");
      return;
    }

    const normalizedEmail = formData.email.toLowerCase().trim();
    const emailDomain = normalizedEmail.split("@")[1];

    if (emailDomain !== ALLOWED_EMAIL_DOMAIN) {
      setError(`Only @${ALLOWED_EMAIL_DOMAIN} email addresses are allowed to sign up.`);
      return;
    }

    if (formData.password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError("Password and confirm password do not match.");
      return;
    }

    try {
      setLoading(true);

      const data = await registerUser({
        email: formData.email,
        password: formData.password,
        confirmPassword: formData.confirmPassword,
        role: formData.role,
      });

      setSuccess(data.message);

    } catch (err) {
      setError(
        err?.response?.data?.message || "Unable to create account. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background:
          "linear-gradient(135deg, #eef6ff 0%, #f5fbfa 50%, #ffffff 100%)",
        px: 1,
        py: 3,
      }}
    >
      <Paper
        elevation={8}
        sx={{
          width: "100%",
          maxWidth: 380,
          p: {
            xs: 3,
            sm: 4,
          },
          borderRadius: 3,
        }}
      >
        <Typography
          variant="h6"
          fontWeight={600}
          textAlign="center"
          gutterBottom
        >
          Create JobPortal Account
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 1 }}>
            {success}
          </Alert>
        )}

        {!success && (
          <Box component="form" onSubmit={handleSubmit}>

            <TextField
              fullWidth
              margin="normal"
              size="small"
              label="Email Address"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              autoComplete="email"
              placeholder={`you@${ALLOWED_EMAIL_DOMAIN}`}
              sx={{
                mb: 0,
                "& input::placeholder": {
                  fontSize: "14px",
                },
              }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailOutlined sx={{
                        fontSize: 18,
                        color: "lightgrey",
                      }} />
                    </InputAdornment>
                  ),
                }
              }}
            />

            <TextField
              fullWidth
              margin="normal"
              select
              size="small"
              label="Role"
              name="role"
              value={formData.role}
              onChange={handleChange}
               sx={{
                mb: 0,
                "& input::placeholder": {
                  fontSize: "14px",
                },
              }}
              slotProps={{
                select: {
                  displayEmpty: true,
                  renderValue: (selected) => {
                    if (!selected) {
                      return (
                        <span style={{ fontSize:"14px",color: "#9e9e9e" }}>
                          Select your role
                        </span>
                      );
                    }

                    return selected === "TEAM_LEAD"
                      ? "Team Lead"
                      : "Recruiter";
                  },
                },

                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <WorkOutlineOutlined sx={{
                        fontSize: 18,
                        color: "lightgrey",
                      }} />
                    </InputAdornment>
                  ),
                }
              }}
            >
              <MenuItem value="">
                Select your role
              </MenuItem>
              <MenuItem value="TEAM_LEAD">
                Team Lead
              </MenuItem>

              <MenuItem value="RECRUITER">
                Recruiter
              </MenuItem>
            </TextField>

            <TextField
              fullWidth
              margin="normal"
              size="small"
              label="Password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              type={showPassword ? "text" : "password"}
              autoComplete="new-password"
              placeholder="Enter your password"
               sx={{
                mb: 0,
                "& input::placeholder": {
                  fontSize: "14px",
                },
              }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlined sx={{
                        fontSize: 18,
                        color: "lightgrey",
                      }} />
                    </InputAdornment>
                  ),
                }
              }}
            />

            <TextField
              fullWidth
              margin="normal"
              size="small"
              label="Confirm Password"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              type={showConfirmPassword ? "text" : "password"}
              autoComplete="new-password"
              placeholder="Confirm your password"
               sx={{
                mb: 0,
                "& input::placeholder": {
                  fontSize: "14px",
                },
              }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlined sx={{
                        fontSize: 18,
                        color: "lightgrey",
                      }} />
                    </InputAdornment>
                  ),
                }
              }}
            />

            <Button
              fullWidth
              type="submit"
              variant="contained"
              size="large"
              disabled={loading}
              sx={{
                height: 38,
                borderRadius: 2,
                textTransform: "none",
                mt: 2,
              }}
            >
              {loading ? (
                <CircularProgress
                  size={24}
                  color="inherit"
                />
              ) : (
                "Create Account"
              )}
            </Button>
          </Box>
        )}

        <Typography
          variant="body2"
          textAlign="center"
          color="text.secondary"
          sx={{ mt: 3 }}
        >
          Already have an account?{" "}
          <Link
            href="/"
            underline="hover"
            fontWeight={600}
          >
            Sign in
          </Link>
        </Typography>
      </Paper>
    </Box>
  );
};

export default Signup;